import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { AppBar, Box, Divider, IconButton, Link, Toolbar, Tooltip, Typography } from '@mui/material';
import CART_ICON from 'assets/img/icon-cart.svg';
import MENU_ICON from 'assets/img/icon-menu.svg';
import USER_ICON from 'assets/img/icon-user.svg';
import classNames from 'classnames';
import { setupContentUrl, setupCurrentOriginUrl } from 'utils/contentUtils';
import styles from './BottomHeader.module.scss';
import useBottomHeader from './useBottomHeader';

const BottomHeader = ({
  appLogoUrl,
  appLogo,
  menuItems,
  toggleMenu,
  cartButtonLabel,
  userButtonLabel,
  menuButtonLabel,
}) => {
  const {
    trigger,
    menuItemsRef,
    activeElement,
    onMenuItemClickSetActiveItem,
    onMouseEnterSetActiveItem,
    onMouseLeaveClearActiveItem,
  } = useBottomHeader();

  return (
    <>
      <AppBar position="sticky" color="default" elevation={0} className="bg-body" sx={{ zIndex: '1000' }}>
        <Box className="container-fluid g-0" sx={{ '@media (min-width:1680px)': { maxWidth: '1570px' } }}>
          <Toolbar
            sx={{
              mx: 'auto',
              width: '100%',
              height: '60px',
              minHeight: '60px !important',
              maxHeight: '60px !important',
              paddingLeft: '0 !important',
              '@media (min-width:992px)': {
                height: '80px',
                minHeight: '80px !important',
                maxHeight: '80px !important',
              },
            }}
          >
            <div className={classNames(styles.Logo, { sticky: trigger })}>
              {typeof appLogoUrl === 'string' && (
                <IconButton
                  component="a"
                  href={appLogoUrl}
                  disableRipple
                  className="h-100 w-100"
                  sx={{
                    p: '.5rem',
                    minWidth: '60px',
                    bgcolor: 'primary.main',
                    borderRadius: 0,
                    '@media (min-width:992px)': {
                      p: trigger ? '.66rem' : '1rem',
                    },
                  }}
                >
                  {typeof appLogo?.url === 'string' && (
                    <img
                      src={setupContentUrl(appLogo.url)}
                      alt={appLogo.alternativeText ?? ''}
                      className="h-100 w-100"
                      style={{ maxWidth: '250px', objectFit: 'contain' }}
                    />
                  )}
                </IconButton>
              )}
            </div>
            <div className="flex-grow-1 flex-shrink-1 ps-4 h-100">
              <Box
                ref={menuItemsRef}
                className="d-none d-lg-flex h-100 align-items-center"
                sx={{
                  gap: '2.5rem',
                  '@media (max-width: 1199px)': { gap: '1.5rem' },
                  '@media (max-width: 1100px)': { gap: '.5rem' },
                }}
              >
                {Array.isArray(menuItems) &&
                  menuItems.map((it) => (
                    <Link
                      key={it.id}
                      href={setupCurrentOriginUrl(it.url) ?? '#'}
                      onMouseEnter={() => onMouseEnterSetActiveItem(it)}
                      onMouseLeave={onMouseLeaveClearActiveItem}
                      onClick={(e) => onMenuItemClickSetActiveItem(e, it)}
                      className={classNames('dektop_main_menu_item px-1 py-2 px-xl-2', styles.MenuItem, {
                        active: activeElement?.id === it.id,
                        hasElements: Array.isArray(it.list) && it.list.length > 0,
                      })}
                    >
                      <Typography component="span" variant="h6" color="inherit">
                        {it.label}
                      </Typography>
                    </Link>
                  ))}
              </Box>
            </div>
            <div className="d-flex align-items-center gap-2 ps-1">
              <div>
                <Tooltip
                  title={cartButtonLabel}
                  slotProps={{
                    tooltip: {
                      className: 'shadow rounded-pill fs-8 px-2 py-1',
                      sx: {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                      },
                    },
                  }}
                >
                  <IconButton color="inherit" component="a" href="/eshop/cartPage/cartPage.jsp">
                    <img alt="" src={CART_ICON} width={32} height={32} />
                  </IconButton>
                </Tooltip>
              </div>
              <div>
                <Tooltip
                  title={userButtonLabel}
                  slotProps={{
                    tooltip: {
                      className: 'shadow rounded-pill fs-8 px-2 py-1',
                      sx: {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                      },
                    },
                  }}
                >
                  <IconButton color="inherit" component="a" href="/selfcare">
                    <img alt="" src={USER_ICON} width={32} height={32} />
                  </IconButton>
                </Tooltip>
              </div>
              <div className="d-block d-lg-none">
                <Tooltip
                  title={menuButtonLabel}
                  slotProps={{
                    tooltip: {
                      className: 'shadow rounded-pill fs-8 px-2 py-1',
                      sx: {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                      },
                    },
                  }}
                >
                  <IconButton color="inherit" onClick={() => toggleMenu()}>
                    <img alt="" src={MENU_ICON} width={32} height={32} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </Toolbar>
        </Box>
        <Divider
          className="main_menu_border border-header-grey opacity-100"
          style={{ borderWidth: '0', borderTopWidth: '1px' }}
          onMouseLeave={onMouseLeaveClearActiveItem}
        />
        {Array.isArray(activeElement?.list) && activeElement.list.length > 0 && (
          <nav
            className={classNames('desktop_main_menu d-none d-lg-block', styles.ActiveSubMenu, { sticky: trigger })}
            onMouseLeave={onMouseLeaveClearActiveItem}
          >
            <div className={classNames('container-fluid g-0', styles.subMenuContainer)}>
              <div className="row g-2">
                {activeElement.list.map((parent) => {
                  const parentUrl = setupCurrentOriginUrl(parent.url);
                  const hasParentLink = parentUrl != null;
                  const hasElements = Array.isArray(parent.list) && parent.list.length > 0;
                  return (
                    <div key={parent.id} className="col">
                      <Link
                        className={classNames('d-flex gap-2 align-items-center', {
                          activeLink: hasParentLink,
                        })}
                        key={parent.id}
                        href={parentUrl ?? '#'}
                        onClick={hasParentLink ? undefined : (e) => e.preventDefault()}
                        color="black"
                        underline={hasParentLink ? 'hover' : 'none'}
                      >
                        <Typography component="div" variant="h6" color="inherit">
                          {parent.label}
                        </Typography>
                        <KeyboardArrowRightIcon />
                      </Link>
                      {hasElements &&
                        parent.list.map((child) => {
                          const childUrl = setupCurrentOriginUrl(child.url);
                          const hasChildLink = childUrl != null;
                          return (
                            <Link
                              className={classNames('d-block pt-4', { activeLink: hasChildLink })}
                              key={child.id}
                              href={childUrl ?? '#'}
                              onClick={hasChildLink != null ? undefined : (e) => e.preventDefault()}
                              color="black"
                              underline={hasChildLink ? 'hover' : 'none'}
                            >
                              <Typography component="span" variant="body1" color="inherit">
                                {child.label}
                              </Typography>
                            </Link>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
          </nav>
        )}
      </AppBar>
    </>
  );
};
export default BottomHeader;
